import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle2, ChevronRight, ExternalLink } from 'lucide-react';
import { contactApi } from '../api/contact';
import './Contact.css';

export function Contact() {
    const { language } = useLanguage();
    const isRtl = language === 'ar';
    const L = (ar, en) => isRtl ? ar : en;

    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
    const [status, setStatus] = useState({ loading: false, success: false });
    const [errors, setErrors] = useState({});

    // ── STRICT ANTI-SPAM VALIDATION ──
    const validate = () => {
        const newErrors = {};
        
        const nameRegex = /^[\p{L}\s]+$/u;
        if (!formData.name.trim() || formData.name.trim().length < 3) {
            newErrors.name = L('يجب أن يحتوي الاسم على 3 أحرف على الأقل', 'Name must be at least 3 characters');
        } else if (!nameRegex.test(formData.name.trim())) {
            newErrors.name = L('عذراً، لا يمكن استخدام رموز أو أرقام في الاسم', 'Name cannot contain symbols or numbers');
        }
        
        if (!formData.phone.trim() || formData.phone.trim().length < 8) {
            newErrors.phone = L('يرجى إدخال رقم هاتف صحيح', 'Please enter a valid phone number');
        }
        
        if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
            newErrors.email = L('يرجى إدخال بريد إلكتروني صحيح', 'Valid email is required');
        }
        
        const spamSymbolsRegex = /([^\p{L}\p{N}\s])\1{3,}/u; 
        const hasLetters = /[\p{L}]/u; 

        if (!formData.message.trim()) {
            newErrors.message = L('يرجى كتابة رسالتك', 'Message is required');
        } else if (spamSymbolsRegex.test(formData.message)) {
            newErrors.message = L('عذراً، يرجى كتابة رسالة صحيحة بدون رموز مكررة', 'Please write a valid message without repetitive symbols');
        } else if (!hasLetters.test(formData.message)) {
            newErrors.message = L('الرسالة يجب أن تحتوي على كلمات حقيقية', 'Message must contain actual words');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) return;

        setStatus({ loading: true, success: false });

        const payload = {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            subject: "New Inquiry from Ayonk Website",
            message: formData.message
        };

        try {
            await contactApi.sendMessage(payload);
            setStatus({ loading: false, success: true });
            setFormData({ name: '', email: '', phone: '', message: '' });
            setErrors({});
            // تمرير الشاشة للأعلى بنعومة
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setStatus({ loading: false, success: false });
            setErrors({ message: L('حدث خطأ، يرجى المحاولة مرة أخرى', 'An error occurred, please try again') });
        }
    };

    const resetFormView = () => {
        setStatus({ loading: false, success: false });
        setFormData({ name: '', email: '', phone: '', message: '' });
        setErrors({});
    };

    if (status.success) {
        return (
            <div className="elite-contact-wrapper" dir={isRtl ? 'rtl' : 'ltr'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: '20px' }}>
                <div style={{ textAlign: 'center', padding: '50px 20px', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.08)', width: '100%', maxWidth: '500px' }}>
                    <CheckCircle2 size={80} color="#d4af37" style={{ marginBottom: '20px', margin: '0 auto' }} />
                    <h2 style={{ fontSize: '28px', marginBottom: '10px', color: '#111', fontWeight: '800' }}>{L('تم الإرسال بنجاح', 'Message Sent')}</h2>
                    <p style={{ color: '#64748b', marginBottom: '30px', fontSize: '15px', lineHeight: '1.6' }}>{L('شكراً لتواصلك. فريقنا سيقوم بالرد عليك قريباً.', 'Thank you. Our team will contact you shortly.')}</p>
                    <button onClick={resetFormView} className="elite-submit-btn" style={{ width: '100%' }}>
                        {L('إرسال رسالة أخرى', 'Send Another Message')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <main className="elite-contact-wrapper" dir={isRtl ? 'rtl' : 'ltr'}>
            <section className="elite-hero-header">
                <div className="elite-breadcrumb-pill">
                    <Link to="/" className="bread-home">{L('الرئيسية', 'Home')}</Link>
                    <span className="bread-sep"><ChevronRight size={14} className={isRtl ? 'icon-flip' : ''} /></span>
                    <span className="bread-contact">{L('تواصل معنا', 'Contact')}</span>
                </div>
                <h1 className="elite-hero-title">{L('كن على اتصال بنا', 'Connect with Us')}</h1>
            </section>

            <div className="elite-contact-container">
                <div className="elite-contact-grid">
                    
                    <div className="elite-info-panel">
                        <h2 className="panel-title">{L('معلومات التواصل', 'Contact Information')}</h2>
                        <p className="panel-desc">{L('نحن هنا للإجابة على استفساراتك وتلبية احتياجات مشروعك.', 'We are here to answer your inquiries and meet your project needs.')}</p>
                        
                        <div className="elite-info-cards">
                            <a href="tel:01022267372" className="elite-info-item clickable">
                                <div className="elite-icon-box"><Phone size={28} /></div>
                                <div>
                                    <span className="elite-item-label">{L('اتصل بنا', 'Call Us')}</span>
                                    <span className="elite-item-value" style={{ direction: 'ltr', display: 'inline-block' }}>01022267372</span>
                                </div>
                            </a>
                            <a href="mailto:ayonkdesigns@gmail.com" className="elite-info-item clickable">
                                <div className="elite-icon-box"><Mail size={28} /></div>
                                <div>
                                    <span className="elite-item-label">{L('البريد الإلكتروني', 'Email')}</span>
                                    <span className="elite-item-value">ayonkdesigns@gmail.com</span>
                                </div>
                            </a>
                            
                            <a href="https://maps.google.com/?q=Ismailia" target="_blank" rel="noreferrer" className="elite-info-item clickable">
                                <div className="elite-icon-box"><MapPin size={28} /></div>
                                <div>
                                    <span className="elite-item-label">{L('فرع الإسماعيلية', 'Ismailia Branch')}</span>
                                    <span className="elite-item-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {L('الإسماعيلية، مصر', 'Ismailia, Egypt')} <ExternalLink size={14} color="#d4af37" />
                                    </span>
                                </div>
                            </a>

                            <a href="https://maps.google.com/?q=Bir+al-Abd" target="_blank" rel="noreferrer" className="elite-info-item clickable">
                                <div className="elite-icon-box"><MapPin size={28} /></div>
                                <div>
                                    <span className="elite-item-label">{L('فرع بئر العبد', 'Bir al-Abd Branch')}</span>
                                    <span className="elite-item-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {L('بئر العبد، شمال سيناء', 'Bir al-Abd, North Sinai')} <ExternalLink size={14} color="#d4af37" />
                                    </span>
                                </div>
                            </a>
                        </div>
                    </div>

                    <div className="elite-form-panel">
                        <h3 className="form-head-title">{L('أرسل استفسارك', 'Quick Inquiry')}</h3>
                        <form onSubmit={handleSubmit} className="elite-form" noValidate>
                            <div className="elite-form-row">
                                <div className="elite-input-group">
                                    <label>{L('الاسم', 'Name')} *</label>
                                    <input 
                                        type="text" 
                                        value={formData.name} 
                                        onChange={e => { setFormData({...formData, name:e.target.value}); setErrors({...errors, name:''}); }}
                                        className={errors.name ? 'input-error' : ''} 
                                    />
                                    {errors.name && <span className="field-error-text">{errors.name}</span>}
                                </div>
                                <div className="elite-input-group">
                                    <label>{L('الجوال', 'Phone')} *</label>
                                    <input 
                                        type="tel" 
                                        value={formData.phone} 
                                        onChange={e => { setFormData({...formData, phone:e.target.value}); setErrors({...errors, phone:''}); }} 
                                        dir="ltr" 
                                        style={{ textAlign: isRtl ? 'right' : 'left' }}
                                        className={errors.phone ? 'input-error' : ''} 
                                    />
                                    {errors.phone && <span className="field-error-text">{errors.phone}</span>}
                                </div>
                            </div>
                            <div className="elite-input-group">
                                <label>{L('البريد الإلكتروني', 'Email')} *</label>
                                <input 
                                    type="email" 
                                    value={formData.email} 
                                    onChange={e => { setFormData({...formData, email:e.target.value}); setErrors({...errors, email:''}); }} 
                                    dir="ltr" 
                                    style={{ textAlign: isRtl ? 'right' : 'left' }}
                                    className={errors.email ? 'input-error' : ''} 
                                />
                                {errors.email && <span className="field-error-text">{errors.email}</span>}
                            </div>
                            <div className="elite-input-group">
                                <label>{L('كيف يمكننا مساعدتك؟', 'Message')} *</label>
                                <textarea 
                                    rows="5" 
                                    value={formData.message} 
                                    onChange={e => { setFormData({...formData, message:e.target.value}); setErrors({...errors, message:''}); }}
                                    className={errors.message ? 'input-error' : ''}
                                ></textarea>
                                {errors.message && <span className="field-error-text">{errors.message}</span>}
                            </div>
                            <button type="submit" className="elite-submit-btn" disabled={status.loading}>
                                {status.loading ? '...' : L('إرسال الرسالة', 'Send Message')} <Send size={18} />
                            </button>
                        </form>
                    </div>
                </div>

                <div className="elite-maps-grid">
                    <div className="wow-map-wrapper">
                        <div className="map-overlay-tag">{L('فرع الإسماعيلية', 'Ismailia Branch')}</div>
                        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d110502.61185040689!2d32.20186455!3d30.60427215!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14f85ecccb5f4bfd%3A0x6543b59301da287e!2sIsmailia%2C%20Ismailia%20Governorate!5e0!3m2!1sen!2seg!4v1712799300000!5m2!1sen!2seg" allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                    </div>
                    <div className="wow-map-wrapper">
                        <div className="map-overlay-tag">{L('فرع بئر العبد', 'Bir al-Abd Branch')}</div>
                        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d54751.27211116663!2d33.0039234!3d31.0116805!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14fdb502129c0111%3A0x8684d0b1ea4c94d6!2sBir%20El%20Abd%2C%20North%20Sinai%20Governorate!5e0!3m2!1sen!2seg!4v1712799350000!5m2!1sen!2seg" allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                    </div>
                </div>
            </div>
        </main>
    );
}
